// ============================================================================
// Check-In Wizard - Main Container Component
// ============================================================================
// Purpose: Multi-step wizard for vehicle check-in process
// Steps: 1) VIN, 2) Photos + Odometer, 3) Signature (retail only), 4) Work Verification (final confirmation)
// ============================================================================

import React, { Component } from 'react';
import { withRouter } from "react-router-dom";
import { HTTPService } from "./../../../services/httpService";
import VINStep from './Vinstep';
import WorkVerificationStep from './WorkVerificationStep';
import PhotosOdometerStep from './PhotosOdometerStep';
import SignatureStep from './SignatureStep';
import ReactGA from 'react-ga4';

const API = new HTTPService();

class CheckInWizard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: 1,
      loading: false,
      
      // Request data
      requestId: null,
      request: null,
      checkin: null,
      services: [],
      
      // Step 1: VIN
      vin: '',
      vinVerified: false,

      // Step 2: Photos & Vehicle Info
      odometerPhoto: null,
      odometerReading: '',
      plate: '',
      conditionPhotos: [],
      photosUploaded: false,

      // Step 3: Signature (retail only)
      signatureData: null,
      signerName: '',
      carfaxOptOut: false,
      carfaxInfo: null,

      // Step 4: Work Verification (final confirmation)
      workVerified: false,
      
      // UI state
      error: null,
      user: this.props.user
    };

    this.handleVINVerified = this.handleVINVerified.bind(this);
    this.handlePhotosUploaded = this.handlePhotosUploaded.bind(this);
    this.handleSignatureComplete = this.handleSignatureComplete.bind(this);
    this.handleWorkVerified = this.handleWorkVerified.bind(this);
    this._next = this._next.bind(this);
    this._prev = this._prev.bind(this);
  }

  async componentDidMount() {
    try {
      const requestId = this.props.match.params.requestId;
      
      if (!requestId) {
        console.error("No request ID provided in URL");
        alert("Invalid check-in request. Redirecting back to requests.");
        this.props.history.push("/requestimates");
        return;
      }

      this.setState({ loading: true });

      console.log("🚀 Starting check-in wizard for request:", requestId);

      // Start check-in on server
      const { data: checkinResponse } = await API.post(`/requests/${requestId}/checkin/start`);

      if (!checkinResponse.success) {
        throw new Error(checkinResponse.error || 'Failed to start check-in');
      }

      // Get full request data
      const { data: requestData } = await API.get(`/requestimates/fullrequest/?Id=${requestId}`);
      const request = requestData[0];

      // Get services for the request
      const { data: services } = await API.get(`/requestService/${requestId}`);

      // Fetch CarFax info for retail jobs
      let carfaxInfo = null;
      try {
        const { data: statusResponse } = await API.get(`/requests/${requestId}/checkin/status`);
        console.log("CarFax status response:", statusResponse);
        const statusData = statusResponse.data || statusResponse;
        if (statusData.carfax_info) {
          carfaxInfo = statusData.carfax_info;
        }
      } catch (err) {
        console.warn("Could not fetch CarFax info:", err.message);
      }

      // Set VIN from request
      const vin = request.vin || '';

      this.setState({
        requestId: requestId,
        request: request,
        checkin: checkinResponse.data,
        services: services || [],
        vin: vin,
        carfaxInfo: carfaxInfo,
        carfaxOptOut: carfaxInfo ? carfaxInfo.carfax_opt_out : false,
        loading: false
      });

      // Analytics
      ReactGA.event({
        category: 'CheckIn',
        action: 'Started',
        label: request.Request_type
      });

    } catch (error) {
      console.error("❌ Error starting check-in:", error);
      alert(`Error starting check-in: ${error.message}. Redirecting back to requests.`);
      this.props.history.push("/requestimates");
    }
  }

  // ============================================================================
  // STEP NAVIGATION
  // ============================================================================

  _next() {
    let currentStep = this.state.currentStep;

    // Determine max steps based on job type
    // Dealer: VIN (1) → Photos (2) → Work (3)
    // Retail: VIN (1) → Photos (2) → Signature (3) → Work (4)
    const maxSteps = this.state.checkin?.requires_signature ? 4 : 3;

    currentStep = currentStep >= maxSteps ? maxSteps : currentStep + 1;
    this.setState({ currentStep: currentStep });

    // Analytics
    ReactGA.event({
      category: 'CheckIn',
      action: 'Step Advanced',
      label: `Step ${currentStep}`
    });
  }

  _prev() {
    let currentStep = this.state.currentStep;
    currentStep = currentStep <= 1 ? 1 : currentStep - 1;
    this.setState({ currentStep: currentStep });
  }

  // ============================================================================
  // STEP HANDLERS
  // ============================================================================

  handleVINVerified(vin) {
    console.log("✅ VIN verified:", vin);
    this.setState({
      vinVerified: true,
      vin: vin
    }, () => {
      this._next();
    });
  }

  handlePhotosUploaded(odometerPhoto, odometerReading, conditionPhotos, plate) {
    console.log("✅ Photos uploaded, plate:", plate);
    this.setState({
      odometerPhoto: odometerPhoto,
      odometerReading: odometerReading,
      conditionPhotos: conditionPhotos,
      plate: plate,
      photosUploaded: true
    }, () => {
      this._next(); // Go to signature (retail) or work verification (dealer)
    });
  }

  handleSignatureComplete(signatureData, signerName) {
    console.log("✅ Signature captured");
    this.setState({
      signatureData: signatureData,
      signerName: signerName
    }, () => {
      this._next(); // Go to work verification (final step)
    });
  }

  handleWorkVerified(carfaxOptOut) {
    console.log("✅ Work verified - completing check-in");
    this.setState({
      workVerified: true,
      carfaxOptOut: carfaxOptOut != null ? carfaxOptOut : this.state.carfaxOptOut
    }, () => {
      this.completeCheckin(); // Complete check-in after final confirmation
    });
  }

  // ============================================================================
  // COMPLETE CHECK-IN
  // ============================================================================

  completeCheckin = async () => {
    try {
      this.setState({ loading: true });

      console.log("🏁 Completing check-in for request:", this.state.requestId);

      // ========================================================================
      // STEP 1: Upload Photos First
      // ========================================================================
      console.log("📤 Step 1/2: Uploading photos...");

      const photosFormData = new FormData();

      // Add odometer photo
      if (this.state.odometerPhoto) {
        if (this.state.odometerPhoto.file) {
          photosFormData.append('odometer_photo', this.state.odometerPhoto.file);
        } else {
          // Convert data URL to blob (from camera capture)
          const blob = await (await fetch(this.state.odometerPhoto.preview)).blob();
          photosFormData.append('odometer_photo', blob, `odometer_${Date.now()}.jpg`);
        }
      }

      // Add condition photos
      for (let i = 0; i < this.state.conditionPhotos.length; i++) {
        const photo = this.state.conditionPhotos[i];

        if (photo.file) {
          photosFormData.append('condition_photos', photo.file);
        } else {
          const blob = await (await fetch(photo.preview)).blob();
          photosFormData.append('condition_photos', blob, `condition_${i + 1}_${Date.now()}.jpg`);
        }
      }

      // Add odometer reading if provided
      if (this.state.odometerReading) {
        photosFormData.append('odometer_reading', this.state.odometerReading);
      }

      // Only upload if there are photos or odometer reading
      const hasPhotos = this.state.odometerPhoto || this.state.conditionPhotos.length > 0;
      const hasOdometerReading = this.state.odometerReading;

      if (hasPhotos || hasOdometerReading) {
        // Upload photos to server
        const { data: photosResponse } = await API.post(
          `/requests/${this.state.requestId}/checkin/photos`,
          photosFormData
        );

        if (!photosResponse.success) {
          throw new Error(photosResponse.error || 'Failed to upload photos');
        }

        console.log("✅ Photos uploaded successfully");
      } else {
        console.log("⏭️ No photos to upload, skipping...");
      }

      // ========================================================================
      // STEP 2: Complete Check-In
      // ========================================================================
      console.log("📝 Step 2/2: Finalizing check-in...");

      const formData = new FormData();

      // Add odometer reading (required for CarFax)
      if (this.state.odometerReading) {
        formData.append('odometer_reading', this.state.odometerReading);
      }

      // Add license plate (required for CarFax)
      if (this.state.plate) {
        formData.append('plate', this.state.plate);
      }

      // Add signature for retail jobs
      if (this.state.checkin?.requires_signature && this.state.signatureData) {
        // Convert signature data URL to blob
        const blob = await (await fetch(this.state.signatureData)).blob();
        formData.append('signature', blob, `signature_${Date.now()}.png`);
        formData.append('signer_name', this.state.signerName);
      }

      // Add CarFax opt-out for retail jobs
      if (this.state.carfaxInfo) {
        formData.append('carfax_opt_out', this.state.carfaxOptOut);
      }

      // Complete check-in on server
      const { data: response } = await API.post(
        `/requests/${this.state.requestId}/checkin/complete`,
        formData
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to complete check-in');
      }

      console.log("✅ Check-in completed successfully");

      // Analytics
      ReactGA.event({
        category: 'CheckIn',
        action: 'Completed',
        label: this.state.request.Request_type
      });

      // Show success message
      alert("Check-in completed successfully!");

      // Redirect to In Progress page (car is now received)
      this.props.history.push(`/jobs?filter=inprogress`);

    } catch (error) {
      console.error("❌ Error completing check-in:", error);
      this.setState({
        loading: false,
        error: error.message
      });
      alert(`Error completing check-in: ${error.message}`);
    }
  };

  // ============================================================================
  // CANCEL HANDLER
  // ============================================================================

  handleCancel = (e) => {
    e.preventDefault();

    if (window.confirm("Are you sure you want to cancel check-in? Progress will be lost.")) {
      ReactGA.event({
        category: 'CheckIn',
        action: 'Cancelled',
        label: this.state.request?.Request_type || 'Unknown'
      });

      this.props.history.push("/jobs?filter=scheduled");
    }
  };

  // ============================================================================
  // NAVIGATION BUTTONS
  // ============================================================================

  get previousButton() {
    let currentStep = this.state.currentStep;

    if (currentStep !== 1) {
      return (
        <button 
          className="btn btn-secondary buttons" 
          type="button" 
          onClick={this._prev}
          disabled={this.state.loading}
        >
          PREVIOUS
        </button>
      );
    }

    return null;
  }

  get nextButton() {
    let currentStep = this.state.currentStep;
    const maxSteps = this.state.checkin?.requires_signature ? 4 : 3;

    // Don't show next button on last step (handled by step component)
    if (currentStep === maxSteps) {
      return null;
    }

    let disabled = true;

    // Step 1: VIN must be verified
    if (currentStep === 1 && this.state.vinVerified) {
      disabled = false;
    }

    // Step 2: Photos must be uploaded
    if (currentStep === 2 && this.state.photosUploaded) {
      disabled = false;
    }

    // Step 3: Signature must be captured (retail only)
    if (currentStep === 3 && this.state.checkin?.requires_signature) {
      // Signature handled by component, don't enable NEXT button
      disabled = true;
    }

    return (
      <button
        className="btn btn-primary float-right buttons"
        type="button"
        disabled={disabled || this.state.loading}
        onClick={this._next}
      >
        {this.state.loading && <i className="fa fa-refresh fa-spin mr-2" />}
        NEXT
      </button>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  render() {
    if (this.state.loading && !this.state.request) {
      return (
        <div className="container mt-5">
          <div className="text-center">
            <i className="fa fa-spinner fa-spin fa-3x text-primary"></i>
            <p className="mt-3">Loading check-in wizard...</p>
          </div>
        </div>
      );
    }

    const maxSteps = this.state.checkin?.requires_signature ? 4 : 3;

    return (
      <div className="container-fluid bg-white border border-secondary mt-3">
        {/* Header */}
        <div className="row">
          <div className="col-lg-12 text-right p-3">
            <button
              className="btn btn-link"
              type="button"
              onClick={this.handleCancel}
              disabled={this.state.loading}
            >
              <i className="fa fa-times fa-2x text-secondary"></i>
            </button>
          </div>
        </div>

        {/* Vehicle Info Header */}
        {this.state.request && (
          <div className="row">
            <div className="col-lg-12 px-4 pb-3">
              <h3 className="text-center text-primary mb-2">
                Vehicle Check-In
              </h3>
              <h5 className="text-center text-secondary">
                {this.state.request.vehicleYear} {this.state.request.vehicleMake} {this.state.request.vehicleModel}
                {this.state.request.vehicleTrim && ` ${this.state.request.vehicleTrim}`}
              </h5>
              {this.state.vin && !this.state.vin.toUpperCase().startsWith('NV') && (
                <p className="text-center text-muted">
                  VIN: {this.state.vin}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="row mb-4">
          <div className="col-lg-12">
            <div className="d-flex justify-content-center align-items-center">
              {[...Array(maxSteps)].map((_, index) => {
                const stepNumber = index + 1;
                const isActive = stepNumber === this.state.currentStep;
                const isCompleted = stepNumber < this.state.currentStep;
                
                return (
                  <React.Fragment key={stepNumber}>
                    <div 
                      className={`
                        rounded-circle 
                        d-flex 
                        align-items-center 
                        justify-content-center 
                        ${isActive ? 'bg-primary text-white' : isCompleted ? 'bg-success text-white' : 'bg-secondary text-white'}
                      `}
                      style={{ width: '40px', height: '40px', fontWeight: 'bold' }}
                    >
                      {isCompleted ? <i className="fa fa-check" /> : stepNumber}
                    </div>
                    {stepNumber < maxSteps && (
                      <div 
                        className={`mx-2 ${isCompleted ? 'bg-success' : 'bg-secondary'}`}
                        style={{ width: '60px', height: '3px' }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="text-center mt-2">
              <small className="text-muted">
                Step {this.state.currentStep} of {maxSteps}
              </small>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {this.state.error && (
          <div className="row">
            <div className="col-lg-12 px-4">
              <div className="alert alert-danger" role="alert">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                {this.state.error}
              </div>
            </div>
          </div>
        )}

        {/* Step Components */}
        <div className="row">
          <div className="col-lg-12 p-4">
            {/* Step 1: VIN Verification */}
            {this.state.currentStep === 1 && (
              <VINStep
                requestId={this.state.requestId}
                vin={this.state.vin}
                onVINVerified={this.handleVINVerified}
                loading={this.state.loading}
              />
            )}

            {/* Step 2: Photos + Odometer + Plate */}
            {this.state.currentStep === 2 && (
              <PhotosOdometerStep
                requestId={this.state.requestId}
                request={this.state.request}
                company={this.props.company}
                onPhotosUploaded={this.handlePhotosUploaded}
                loading={this.state.loading}
                requiresSignature={this.state.checkin?.requires_signature}
              />
            )}

            {/* Step 3: Signature (Retail Only) */}
            {this.state.currentStep === 3 && this.state.checkin?.requires_signature && (
              <SignatureStep
                requestId={this.state.requestId}
                request={this.state.request}
                checkin={this.state.checkin}
                onSignatureComplete={this.handleSignatureComplete}
                loading={this.state.loading}
              />
            )}

            {/* Step 3 (Dealer) or Step 4 (Retail): Work Verification - Final Confirmation */}
            {((this.state.currentStep === 3 && !this.state.checkin?.requires_signature) ||
              (this.state.currentStep === 4 && this.state.checkin?.requires_signature)) && (
              <WorkVerificationStep
                requestId={this.state.requestId}
                request={this.state.request}
                services={this.state.services}
                carfaxInfo={this.state.carfaxInfo}
                onWorkVerified={this.handleWorkVerified}
                loading={this.state.loading}
              />
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="row">
          <div className="col-lg-12 p-4">
            <div className="d-flex justify-content-between">
              <div>
                {this.previousButton}
              </div>
              <div>
                {this.nextButton}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(CheckInWizard);